import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/errors/use-toast';

export interface AddRelatedQuestionPayload {
  researchId: string;
  questionText: string;
}

export const useAddRelatedQuestion = () => {
  const queryClient = useQueryClient();

  const addRelatedQuestionMutation = useMutation({
    mutationFn: async (payload: AddRelatedQuestionPayload) => {
      console.log('[useAddRelatedQuestion] Adding new related question:', payload);

      // First, get the current research to access its related_questions
      const { data: currentResearch, error: fetchError } = await supabase
        .from('research_library')
        .select('related_questions')
        .eq('id', payload.researchId)
        .single();

      if (fetchError) {
        console.error('[useAddRelatedQuestion] Error fetching current research:', fetchError);
        throw new Error(`Failed to fetch current research: ${fetchError.message}`);
      }

      if (!currentResearch) {
        throw new Error('Research not found');
      }

      // Get the current related questions array - handle both old string[] and new mixed format
      let currentQuestions: any[] = [];
      if (Array.isArray(currentResearch.related_questions)) {
        currentQuestions = currentResearch.related_questions;
      } else if (currentResearch.related_questions) {
        // Handle case where it might be stored as jsonb but needs parsing
        try {
          currentQuestions = JSON.parse(JSON.stringify(currentResearch.related_questions));
        } catch (parseError) {
          console.warn('[useAddRelatedQuestion] Failed to parse related_questions, treating as empty array');
          currentQuestions = [];
        }
      }

      // Create the updated questions array with the new question at the beginning
      const updatedQuestions = [payload.questionText, ...currentQuestions];

      console.log('[useAddRelatedQuestion] Updated questions array:', updatedQuestions);

      // Update the research in the database
      const { data, error: updateError } = await supabase
        .from('research_library')
        .update({
          related_questions: updatedQuestions,
          updated_at: new Date().toISOString()
        })
        .eq('id', payload.researchId)
        .select()
        .single();

      if (updateError) {
        console.error('[useAddRelatedQuestion] Error updating research:', updateError);
        throw new Error(`Failed to update research: ${updateError.message}`);
      }

      console.log('[useAddRelatedQuestion] Research updated successfully:', data);
      return data;
    },
    onSuccess: (data, payload) => {
      console.log('[useAddRelatedQuestion] Mutation succeeded, invalidating queries');
      
      // Invalidate the specific research details query to update the lightbox sidebar
      queryClient.invalidateQueries({
        queryKey: ['research-details', payload.researchId]
      });
      
      // Also invalidate the research item details query used by useResearchItemDetails
      queryClient.invalidateQueries({
        queryKey: ['research-item-details', payload.researchId]
      });
      
      // Invalidate the research library queries to ensure the updated data is reflected
      queryClient.invalidateQueries({
        queryKey: ['research-library']
      });

      toast({
        title: 'Question added',
        description: 'Your custom question has been added to the related questions',
      });
    },
    onError: (error: any) => {
      console.error('[useAddRelatedQuestion] Mutation failed:', error);
      toast({
        title: 'Failed to add question',
        description: error.message || 'Failed to add the question to related questions',
        variant: 'destructive',
      });
    }
  });

  return {
    addRelatedQuestion: addRelatedQuestionMutation.mutate,
    isAdding: addRelatedQuestionMutation.isPending,
    error: addRelatedQuestionMutation.error
  };
}; 